import React, { useMemo } from "react";
import "./BlueprintModeGrid.scss";

// ═════════════════════════════════════════════════════════════════════════════
// Blueprint Mode grid — the Art of Scale "Execution Blueprint" wall chart.
//
// ⚠️ THIS FILE IS INTENTIONALLY IDENTICAL IN TWO REPOSITORIES.
//
//   10x-coach-frontend/src/components/CoachPanel/ExecutionBlueprint/
//     ExecutionBlueprintLiveReview/_shared/BlueprintModeGrid.js
//   10x-frontend/src/components/UserPanel/ExecutionBlueprint/
//     ExecutionBlueprintLiveReview/_shared/BlueprintModeGrid.js
//
// Same rule and same reason as `_socket/LiveReviewSocketStore.js` and
// `_socket/liveReviewAgenda.js`: the coach and the CEO look at this grid in the
// same meeting, and a difference between the two copies shows up as two people
// disagreeing about their own blueprint. `diff` is the test. **Change one copy,
// change both in the same pass.**
//
// Deliberately depends on NOTHING but React and its own stylesheet — no icon
// import, no enum import, no design-token import. That is what makes the two
// copies portable: the coach repo has Strategy Canvas to borrow icons and
// tokens from and the CEO repo does not, so anything imported here would have to
// resolve differently in the two apps and the files could not stay identical.
// Hence the inlined icons, the inlined status strings and the self-contained
// SCSS. See the SCSS header for the same note about design tokens.
//
// ── WHY THIS EXISTS AT ALL ───────────────────────────────────────────────────
// The coach app already has `ExecutionBlueprintBlueprintMode`, which fetches
// `GET /coach/execution-blueprint/{uniqueId}/blueprint-mode` and renders this
// same chart. That component is right for the ASYNC review screen and is still
// used there, untouched.
//
// It cannot serve the live session, for two separate reasons:
//   1. **The CEO can't call that endpoint.** It sits behind `CoachAuthGuard` and
//      403s for a CEO, so the CEO app has no REST route to this data at all.
//      Its only source is the live socket snapshot.
//   2. **A REST read is stale mid-meeting.** It returns the blueprint as last
//      stored, so an item edited during the session still shows its old value —
//      which, on a screen whose whole purpose is editing together, reads as the
//      edit having been lost.
//
// So this is presentational only: it takes `sections` and renders them. Whoever
// mounts it decides where they came from. Both live screens pass the socket
// snapshot's, which makes the grid live on both sides and identical on both
// sides — the two properties the async component could not have given us.
// ═════════════════════════════════════════════════════════════════════════════

// ⚠️ `REVIEW_STATUS_ACCEPTED` and `AUTHORING_NOTICE` were deleted here on
// 2026-09-03 with the two status markers they fed (see `ItemCard`). They were
// the backend's own strings (`08-fe-handoff.md` §1/§2), inlined rather than
// imported so this file stays byte-identical across the two repos — that
// reasoning still applies to everything else inlined below.

// Positional, not keyed by section name — there are always exactly four
// sections in a fixed order (`financial_wig`, `customer_wig`, `operations_wig`,
// `people_wig`), so this never needs to know how they are spelled and cannot
// break if the backend renames one.
const COLUMN_ACCENTS = ["one", "two", "three", "four"];

// ── Inlined icons ───────────────────────────────────────────────────────────
// Small enough to carry, and carrying them is what keeps this file identical
// across the two repos. `currentColor` throughout so the chips' own colour
// rules drive them.
// ⚠️ `WarnGlyph`, `TickGlyph` and `GridStatusPill` were deleted here on
// 2026-09-03 with the status markers they drew. `CommentGlyph` survives
// because the comment count does — see `ItemCard`. The inlining rule below
// is unchanged and still applies to it.

const CommentGlyph = ({ size = 11 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
    <path
      d="M14 9.6a1.6 1.6 0 0 1-1.6 1.6H4.8L2 14V3.2a1.6 1.6 0 0 1 1.6-1.6h8.8A1.6 1.6 0 0 1 14 3.2v6.4Z"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const GridSkeleton = () => (
  <div className="bpmg-grid bpmg-skeleton" aria-hidden="true">
    <div className="bpmg-bhag bpmg-shimmer" />
    <div className="bpmg-columns">
      {[0, 1, 2, 3].map((column) => (
        <div className="bpmg-column" key={column}>
          <div className="bpmg-column-header bpmg-shimmer" />
          <div className="bpmg-column-body">
            {[0, 1, 2].map((card) => (
              <div className="bpmg-card bpmg-card-skeleton" key={card}>
                <div className="bpmg-shimmer bpmg-shimmer-line bpmg-w-70" />
                <div className="bpmg-shimmer bpmg-shimmer-line bpmg-w-100" />
                <div className="bpmg-shimmer bpmg-shimmer-line bpmg-w-40" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const ItemCard = ({ item, meta }) => {
  const commentCount = meta?.commentCount || 0;

  return (
    <div className="bpmg-card">
      <div className="bpmg-card-top">
        <span className="bpmg-card-label">{item.label}</span>
      </div>

      {item.value ? (
        <p className="bpmg-card-value">{item.value}</p>
      ) : (
        <p className="bpmg-card-value bpmg-card-value--empty">
          Not filled in yet
        </p>
      )}

      {/* ⚠️ NO "Why:" LINE (2026-09-02, on instruction). The card used to render
          `item.rationale` here. `rationale` is still on the item and still
          reaches this component — it is only not drawn, so nothing about the
          payload or the adapter changed and it can come back as a one-line
          restore. Consistent with the WIG editor, which stopped offering the
          rationale as an editable field the same week. */}

      {/* ⚠️ NO STATUS OF EITHER KIND (2026-09-03, on instruction). Both
          markers went, and they were different axes: the coach's accept /
          to-discuss verdict (`GridStatusPill`) and the CEO's authoring state
          (`AUTHORING_NOTICE` → the warning chip). Only the comment count
          remains, which points at a conversation rather than reporting a
          status.

          Applied here as well as to the coach's own Blueprint Mode so the two
          agree — this grid backs the Live Review Blueprint tab AND the locked
          read-only view, so leaving it out would have meant three Blueprint
          surfaces with two different rules. */}

      {commentCount > 0 && (
        <div className="bpmg-card-meta">
          <span className="bpmg-comment-chip">
            <CommentGlyph />
            {commentCount}
          </span>
        </div>
      )}
    </div>
  );
};

/**
 * @param sections    Normalised `sections[]` — each `{ section_key, label,
 *                    sequence, items[] }`. Straight off the live snapshot.
 * @param bhag        Optional. Rendered as a banner only if truthy; see below.
 * @param footnote    Optional line under the grid. Each app words it for its
 *                    own reader, which is why it's a prop and not baked in.
 * @param loading     Show the skeleton instead of the grid.
 * @param reviewIndex Optional `{ [itemId]: { commentCount } }`.
 *                    Omit it and each item's own `review_status` / `comments`
 *                    are used — which is what the live snapshot carries. The
 *                    override exists for the coach's async screen, where the
 *                    read-only payload has no review affordances and the
 *                    statuses have to be cross-read from a different payload.
 */
const BlueprintModeGrid = ({
  sections,
  bhag,
  footnote,
  loading = false,
  reviewIndex = null,
}) => {
  const list = useMemo(
    () =>
      [...(sections || [])].sort(
        (a, b) => (a.sequence ?? 0) - (b.sequence ?? 0),
      ),
    [sections],
  );

  // Built once for the whole grid rather than per card. When `reviewIndex` is
  // supplied it wins outright; otherwise every item describes itself, which is
  // the live-snapshot case.
  const metaFor = useMemo(() => {
    if (reviewIndex)
      return (item) => reviewIndex[item.execution_blueprint_item_id];
    return (item) => ({
      commentCount: (item.comments || []).length,
    });
  }, [reviewIndex]);

  if (loading && list.length === 0) return <GridSkeleton />;

  if (list.length === 0) {
    return (
      <div className="bpmg-grid">
        <div className="bpmg-empty-state">
          This blueprint doesn&apos;t have any sections to show yet.
        </div>
      </div>
    );
  }

  return (
    <div className="bpmg-grid">
      {/* BHAG is in NO coach contract anywhere — not in the blueprint-mode
          payload, not in the live snapshot's documented shape. The socket
          store's normaliser looks for it speculatively, so this renders only if
          it actually turns up. No placeholder in its absence: an empty teal bar
          reads as missing data rather than as a field that was never here. */}
      {bhag && (
        <div className="bpmg-bhag">
          <span className="bpmg-bhag-cap">BHAG</span>
          <span className="bpmg-bhag-value">{bhag}</span>
        </div>
      )}

      <div className="bpmg-columns">
        {list.map((section, index) => {
          const items = section.items || [];
          return (
            <div
              className={`bpmg-column bpmg-accent-${
                COLUMN_ACCENTS[index % COLUMN_ACCENTS.length]
              }`}
              key={section.section_key || index}
            >
              <div className="bpmg-column-header">
                <span className="bpmg-column-title">{section.label}</span>
                <span className="bpmg-column-count">
                  {items.length} {items.length === 1 ? "item" : "items"}
                </span>
              </div>

              <div className="bpmg-column-body">
                {items.length === 0 ? (
                  <div className="bpmg-column-empty">
                    Nothing captured in this section yet.
                  </div>
                ) : (
                  items.map((item) => (
                    <ItemCard
                      key={item.execution_blueprint_item_id}
                      item={item}
                      meta={metaFor(item)}
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      {footnote && <div className="bpmg-footnote">{footnote}</div>}
    </div>
  );
};

export default BlueprintModeGrid;
